import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Trash2, Check, Play, Pause } from 'lucide-react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
export function AudioRecorder({ onRecorded, disabled }) {
    const { isRecording, duration, blob, start, stop, reset } = useAudioRecorder();
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const objectUrlRef = useRef(null);
    // Cleanup object URL on unmount or blob change
    useEffect(() => {
        return () => {
            if (objectUrlRef.current)
                URL.revokeObjectURL(objectUrlRef.current);
            audioRef.current?.pause();
        };
    }, [blob]);
    const togglePlay = useCallback(() => {
        if (!blob)
            return;
        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
            return;
        }
        if (!audioRef.current) {
            if (objectUrlRef.current)
                URL.revokeObjectURL(objectUrlRef.current);
            const url = URL.createObjectURL(blob);
            objectUrlRef.current = url;
            const audio = new Audio(url);
            audio.onended = () => setPlaying(false);
            audioRef.current = audio;
        }
        audioRef.current.currentTime = 0;
        audioRef.current.play();
        setPlaying(true);
    }, [blob, playing]);
    const handleReset = useCallback(() => {
        audioRef.current?.pause();
        audioRef.current = null;
        if (objectUrlRef.current)
            URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
        setPlaying(false);
        reset();
    }, [reset]);
    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };
    // Recording done — show play/confirm/discard
    if (blob) {
        return (_jsxs("div", { className: "flex items-center gap-3 bg-white/[.08] border border-emerald-400/20 rounded-xl px-4 py-3", children: [_jsx("button", { onClick: togglePlay, className: "w-8 h-8 rounded-full flex items-center justify-center shrink-0", style: {
                        background: playing ? 'rgba(236,72,153,.7)' : 'rgba(255,255,255,.15)',
                    }, children: playing ? (_jsx(Pause, { className: "w-4 h-4 text-white" })) : (_jsx(Play, { className: "w-4 h-4 text-white ml-0.5" })) }), _jsxs("div", { className: "flex items-center gap-2 flex-1", children: [_jsx("div", { className: "w-2 h-2 rounded-full bg-emerald-400" }), _jsxs("span", { className: "text-emerald-400/80 text-[13px] font-medium", children: [formatTime(duration), " \u0437\u0430\u043F\u0438\u0441\u0430\u043D\u043E"] })] }), _jsx("button", { onClick: handleReset, className: "w-8 h-8 rounded-full flex items-center justify-center bg-white/[.12]", children: _jsx(Trash2, { className: "w-4 h-4 text-white/55" }) }), _jsx("button", { onClick: () => onRecorded(blob, duration), className: "w-8 h-8 rounded-full flex items-center justify-center bg-emerald-400/80", children: _jsx(Check, { className: "w-4 h-4 text-white" }) })] }));
    }
    // Recording in progress
    if (isRecording) {
        return (_jsxs("div", { className: "flex items-center gap-3 border rounded-xl px-4 py-3", style: {
                background: 'rgba(236,72,153,.06)',
                border: '1px solid rgba(236,72,153,.3)',
            }, children: [_jsxs("div", { className: "flex items-center gap-2 flex-1", children: [_jsx("div", { className: "w-2.5 h-2.5 rounded-full bg-red-500", style: { animation: 'titleBlink 1s step-end infinite' } }), _jsx("span", { className: "text-white/70 text-[13px] font-semibold font-mono", children: formatTime(duration) }), _jsx("span", { className: "text-white/50 text-[11px]", children: "/ 0:15" }), _jsx("div", { className: "flex items-center gap-px ml-2", children: [3, 5, 8, 6, 4, 7, 5, 3, 6, 8, 4, 5].map((h, i) => (_jsx("div", { className: "w-[2px] rounded-full bg-pink-400/60", style: {
                                    height: h,
                                    animation: `vib${(i % 4) + 1} ${1 + (i % 3) * 0.3}s ease-in-out infinite`,
                                } }, i))) })] }), _jsx("button", { onClick: stop, className: "w-10 h-10 rounded-full flex items-center justify-center", style: {
                        background: 'rgba(236,72,153,.8)',
                        boxShadow: '0 0 12px rgba(236,72,153,.3)',
                    }, children: _jsx(Square, { className: "w-4 h-4 text-white", fill: "white" }) })] }));
    }
    // Idle — start recording
    return (_jsxs("div", { onClick: disabled ? undefined : start, className: "flex items-center gap-3 bg-white/[.08] border border-white/[.12] rounded-xl px-4 py-3 cursor-pointer active:scale-[0.97] transition-transform", style: { opacity: disabled ? 0.4 : 1 }, children: [_jsx("div", { className: "w-10 h-10 rounded-full bg-pink-500 text-white flex items-center justify-center shrink-0", style: { boxShadow: '0 0 12px rgba(236,72,153,.25)' }, children: _jsx(Mic, { className: "w-5 h-5" }) }), _jsxs("div", { children: [_jsx("p", { className: "text-[13px] text-white/70 font-semibold", children: "\u0417\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u0433\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0435" }), _jsx("p", { className: "text-[10px] text-white/50", children: "15 \u0441\u0435\u043A \u2014 \u0440\u0430\u0441\u0441\u043A\u0430\u0436\u0438 \u0437\u0430\u0447\u0435\u043C \u0432\u0441\u0442\u0440\u0435\u0447\u0430\u0442\u044C\u0441\u044F" })] })] }));
}
